import { CARD_SIZES, PAPER_SIZES } from './constants';
import { downsampleToPanel } from './canvas';
import { effectiveCard } from './geometry';
import type { ExampleCase } from './exampleCards';
import type { AppState } from '../hooks/useAppState';
import type { SlotId } from '../types';

const fetchAsBlob = async (url: string): Promise<Blob> => {
  const r = await fetch(url, { mode: 'cors' });
  if (!r.ok) throw new Error(`Failed to fetch ${url}: ${r.status}`);
  return await r.blob();
};

/** Load a complete demo case: applies card size + paper + orientation +
 *  two-sided, then fetches each demo image from Cloudflare and assigns it
 *  to its slot.  All four image processings run in parallel. */
export const loadSampleCase = async (
  example: ExampleCase,
  app: AppState
): Promise<void> => {
  const cardSize = CARD_SIZES[example.cardSizeId];
  const paperSize = PAPER_SIZES[example.paperSizeId];
  const eff = effectiveCard(cardSize, example.orientation);

  // Apply settings first. These setters are async (they recrop any existing
  // slots) but since we clear and replace below, that doesn't matter.
  await app.setCardSize(cardSize);
  app.setPaperSize(paperSize);
  await app.setOrientation(example.orientation);
  if (example.twoSided !== app.insideEnabled) {
    app.setInsideEnabled(example.twoSided);
  }

  // Fetch + crop each slot's image in parallel. We pass downsampleToPanel
  // the effective panel dims so the resulting JPEGs go straight into the
  // app's normal pipeline (no further re-crop needed).
  const slotEntries = Object.entries(example.slots) as Array<[SlotId, string]>;
  const filtered = example.twoSided
    ? slotEntries
    : slotEntries.filter(([id]) => id === 'front' || id === 'back');

  const results = await Promise.all(
    filtered.map(async ([slotId, url]) => {
      try {
        const blob = await fetchAsBlob(url);
        const slotImg = await downsampleToPanel(blob, eff.widthPx, eff.heightPx);
        return [slotId, slotImg] as const;
      } catch (err) {
        // Keep going if a single image fails; the others still load.
        console.warn(`sample load failed for ${slotId}:`, err);
        return [slotId, null] as const;
      }
    })
  );

  for (const [slotId, img] of results) {
    if (img) app.setSlot(slotId, img);
  }
};
